import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AudioSettingsState } from '../../store/settings/audio-settings/audio-settings.state';
import { AudioService } from './audio.service';
import { Howl } from 'howler';
import { RingerService } from './ringer.service';
import { finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { from, iif, interval, Observable, of } from 'rxjs';
import _ from 'lodash';

@Injectable({
   providedIn: 'root',
})
export class AudioDeviceTestService {
   private ringingDeviceTestPlayers: Set<Howl> = new Set<Howl>();
   private outputDeviceTestPlayers: Set<Howl> = new Set<Howl>();

   private players: Set<Howl> = new Set<Howl>();

   public inputDeviceVolume: Observable<number>;

   constructor(private store: Store, private audioService: AudioService, private ringerService: RingerService) {
      this.store
         .select(AudioSettingsState.isTestActiveForRingingDevice)
         .pipe(
            switchMap((isTestActiveForInputDevice) =>
               iif(
                  () => isTestActiveForInputDevice,
                  this.audioService.chosenRingerDevices$.pipe(
                     tap(() => {
                        this.stopTestSound(this.ringingDeviceTestPlayers);
                     }),
                     tap((ringerDevices) => {
                        ringerDevices.forEach((x) => {
                           this.startTestSoundForDevice(x.deviceId, this.ringingDeviceTestPlayers);
                        });
                     })
                  ),
                  of(1).pipe(tap(() => this.stopTestSound(this.ringingDeviceTestPlayers)))
               )
            )
         )
         .subscribe();

      this.store
         .select(AudioSettingsState.isTestActiveForOutputDevice)
         .pipe(
            switchMap((isTestActiveForInputDevice) =>
               iif(
                  () => isTestActiveForInputDevice,
                  this.audioService.chosenAudioOutputDevice$.pipe(
                     tap(() => {
                        this.stopTestSound(this.outputDeviceTestPlayers);
                     }),
                     tap((outputDevice) => {
                        this.startTestSoundForDevice(outputDevice.deviceId, this.outputDeviceTestPlayers);
                     })
                  ),
                  of(1).pipe(tap(() => this.stopTestSound(this.outputDeviceTestPlayers)))
               )
            )
         )
         .subscribe();

      this.inputDeviceVolume = this.audioService.chosenAudioInputDevice$.pipe(
         switchMap((device) => this.startInputTestForDeviceId$(device.deviceId)),
         share()
      );
   }

   public startInputTestForDeviceId$(deviceId): Observable<number> {
      if (_.isEmpty(deviceId)) {
         return;
      }
      const deviceConstraints: MediaStreamConstraints = {
         audio: { deviceId: { exact: deviceId } },
         video: false,
      };

      return from(navigator.mediaDevices.getUserMedia(deviceConstraints)).pipe(
         map((mediaStream) => {
            const audioContext = new AudioContext();

            const vuMeterAnalyzer = audioContext.createAnalyser();
            const vuMeterMicrophone = audioContext.createMediaStreamSource(mediaStream);

            vuMeterAnalyzer.smoothingTimeConstant = 0.8;
            vuMeterAnalyzer.fftSize = 512;

            vuMeterMicrophone.connect(vuMeterAnalyzer);

            return { vuMeterAnalyzer, vuMeterMicrophone, audioContext, mediaStream };
         }),
         switchMap((analyzerContext) =>
            interval(100).pipe(
               map((x) => {
                  const dataArray = new Uint8Array(analyzerContext.vuMeterAnalyzer.fftSize);
                  analyzerContext.vuMeterAnalyzer.getByteFrequencyData(dataArray);
                  return dataArray;
               }),
               map((dataArray) => _.max(dataArray)),
               map((maxValue) => maxValue / 255),
               finalize(() => {
                  analyzerContext.mediaStream.getTracks().forEach((track) => track.stop());
                  return analyzerContext.audioContext.close();
               })
            )
         )
      );
   }

   public startTestSoundForDevice(deviceId: string, playerCollection: Set<Howl>) {
      const howler = this.ringerService.createHowl(deviceId);
      console.log('Play for device', deviceId);
      howler.play();
      playerCollection.add(howler);
   }

   public stopTestSound(playerCollection: Set<Howl>) {
      playerCollection.forEach((x) => x.stop());
      playerCollection.clear();
   }
}
